import { Form, Field, TextInput, Dropdown, useFormState } from '@carboledger/ui';
import { useTranslations } from '@/hooks';
import { GHG_DEFAULT_VALUE } from '../../constants/validateDeclaration.constants';
import { declarationValidation } from '../../declarationsValidation';

const NumberField = ({ name, label }) => {
  const t = useTranslations();
  return (
    <Field name={name} rules={declarationValidation[name]}>
      {({ id, value, onChange, onBlur, status, message }) => (
        <TextInput
          id={id}
          name={name}
          label={label}
          placeholder={t('FORM_ENTER_EMISSIONS_PLACEHOLDER')}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          status={status}
          message={message}
        />
      )}
    </Field>
  );
};

/**
 * Props:
 *   formOptions  useValidateDeclarationFormOptions() result — supplies the Yes/No options
 */
export const GhgEmissionDetailsSection = ({ formOptions }) => {
  const t = useTranslations();
  const { values } = useFormState();
  const isDefaultNo = values.defaultGhgValueApplied === GHG_DEFAULT_VALUE.no;

  return (
    <Form.Section title={t('SECTION_GHG_EMISSION_DETAILS')}>
      <Form.Row columns={1}>
        <Field name="defaultGhgValueApplied" required>
          {({ id, name, value, onChange, onBlur, status, message, required }) => (
            <Dropdown
              id={id}
              name={name}
              label={t('FIELD_DEFAULT_GHG_VALUE_APPLIED')}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              status={status}
              message={message}
              required={required}
              options={formOptions.ghgDefaultOptions}
            />
          )}
        </Field>
      </Form.Row>

      <Form.Row columns={1}>
        <Field name="totalGhgEmissions" required rules={declarationValidation.totalGhgEmissions}>
          {({ id, name, value, onChange, onBlur, status, message, required }) => (
            <TextInput
              id={id}
              name={name}
              label={t('FIELD_TOTAL_GHG_EMISSIONS')}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              status={status}
              message={message}
              required={required}
            />
          )}
        </Field>
      </Form.Row>

      {isDefaultNo ? (
        <>
          <Form.Row columns={2}>
            <NumberField name="eec" label={t('FIELD_EEC_EMISSIONS')} />
            <NumberField name="el" label={t('FIELD_EL_EMISSIONS')} />
          </Form.Row>
          <Form.Row columns={2}>
            <NumberField name="ep" label={t('FIELD_EP_EMISSIONS')} />
            <NumberField name="etd" label={t('FIELD_ETD_EMISSIONS')} />
          </Form.Row>
          <Form.Row columns={2}>
            <NumberField name="eu" label={t('FIELD_EU_EMISSIONS')} />
            <NumberField name="escaValue" label={t('FIELD_ESCA_EMISSIONS')} />
          </Form.Row>
          <Form.Row columns={2}>
            <NumberField name="eccs" label={t('FIELD_ECCS_EMISSIONS')} />
            <NumberField name="eccr" label={t('FIELD_ECCR_EMISSIONS')} />
          </Form.Row>
        </>
      ) : null}

      <Form.Row columns={2}>
        <Field name="allocatedHeat" rules={declarationValidation.allocatedHeat}>
          {({ id, name, value, onChange, onBlur, status, message }) => (
            <TextInput
              id={id}
              name={name}
              label={t('FORM_ALLOCATED_HEAT')}
              placeholder={t('FORM_ALLOCATED_HEAT_PLACEHOLDER')}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              status={status}
              message={message}
            />
          )}
        </Field>

        <Field name="allocatedElectricity" rules={declarationValidation.allocatedElectricity}>
          {({ id, name, value, onChange, onBlur, status, message }) => (
            <TextInput
              id={id}
              name={name}
              label={t('FORM_ALLOCATED_ELECTRICITY')}
              placeholder={t('FORM_ALLOCATED_ELECTRICITY_PLACEHOLDER')}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              status={status}
              message={message}
            />
          )}
        </Field>
      </Form.Row>

      <Form.Row columns={1}>
        <Field name="escaBonusCap" required rules={declarationValidation.escaBonusCap}>
          {({ id, name, value, onChange, onBlur, status, message, required }) => (
            <Dropdown
              id={id}
              name={name}
              label={t('FORM_ESCA_BONUS_CAP')}
              placeholder={t('COMMON_SELECT')}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              status={status}
              message={message}
              required={required}
              options={formOptions.escaBonusCapOptions}
            />
          )}
        </Field>
      </Form.Row>

      <Form.Row columns={1}>
        <Field name="nuts2Area">
          {({ id, name, value, onChange, onBlur, status, message }) => (
            <TextInput
              id={id}
              name={name}
              label={t('FORM_NUTS2_AREA')}
              placeholder={t('FORM_NUTS2_AREA_PLACEHOLDER')}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              status={status}
              message={message}
            />
          )}
        </Field>
      </Form.Row>
    </Form.Section>
  );
};
